import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { getFullName } from '@core/util/string';
import {
	BusinessAddress,
	BusinessUser,
	DefaultDashboardSelection,
	DefaultDashboardSelectionType,
} from '@models/businesses';
import { Select } from '@ngxs/store';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { BusinessAddressesState } from '@state/business/address';
import { DashboardDataDispatch } from '@state/dashboard/dashboard.dispatch';
import { DashboardState } from '@state/dashboard/dashboard.state';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
	DropdownResult,
	SelectionGroup,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';

interface ItemRow {
	groupType: DefaultDashboardSelectionType;
	id?: string;
	name: string;
}

const allItem = { name: 'All', value: 'All' };

const defaultDashboardFilterValue: DefaultDashboardSelection = {
	type: DefaultDashboardSelectionType.ALL,
	id: null,
};

@Component({
	selector: 'app-view-as-dropdown',
	templateUrl: './view-as-dropdown.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ViewAsDropdownComponent implements OnInit {
	@Select(BusinessAddressesState.getAll) private addresses$!: Observable<
		BusinessAddress[]
	>;
	@Select(BusinessUsersState.getClerks) private clerks$!: Observable<
		BusinessUser[]
	>;
	@Select(DashboardState.getFilter)
	private filter$: Observable<DefaultDashboardSelection>;
	@Select(BusinessUsersState.getLawyers) private lawyers$!: Observable<
		BusinessUser[]
	>;

	itemGroups$!: Observable<SelectionGroup[]>;

	selectedFilterText$!: Observable<string>;

	constructor(
		private dashboardDispatch: DashboardDataDispatch,
		private analytics: AnalyticsService,
	) {}

	ngOnInit(): void {
		this.itemGroups$ = combineLatest([
			this.clerks$,
			this.lawyers$,
			this.addresses$,
		]).pipe(
			map(([clerks, lawyers, addresses]) => [
				{
					items: [allItem],
					type: DefaultDashboardSelectionType.ALL,
				},
				{
					title: 'Offices',
					items: addresses.map((address) =>
						this.addressToItemRow(
							address,
							DefaultDashboardSelectionType.OFFICE,
						),
					),
					type: DefaultDashboardSelectionType.OFFICE,
				},
				{
					title: 'Lawyers',
					items: lawyers.map((lawyer) =>
						this.personToItemRow(lawyer, DefaultDashboardSelectionType.LAWYER),
					),
					type: DefaultDashboardSelectionType.LAWYER,
				},
				{
					title: 'Clerks',
					items: clerks.map((clerk) =>
						this.personToItemRow(clerk, DefaultDashboardSelectionType.CLERK),
					),
					type: DefaultDashboardSelectionType.CLERK,
				},
			]),
		);

		this.selectedFilterText$ = combineLatest([
			this.itemGroups$, // from above
			this.filter$,
		]).pipe(
			map(([itemGroups, filter]) => {
				const all = allItem.name;

				if (!filter) {
					this.dashboardDispatch.Filter(defaultDashboardFilterValue);
					return all;
				}

				const rows: ItemRow[] = [].concat(
					...itemGroups.map((row) => row.items),
				);

				return (
					rows.find(
						(row) => row.groupType === filter?.type && row.id === filter?.id,
					)?.name || all
				);
			}),
		);
	}

	onSelect(ev: DropdownResult<BusinessAddress | BusinessUser>) {
		const filter: DefaultDashboardSelection = {
			type: ev.groupType,
			id: ev.result?.id ?? null,
		};

		this.dashboardDispatch.Filter(filter);

		this.analytics.logEvent(
			AnalyticsEvent.DASHBOARD_HEADER_VIEW_AS_DROPDOWN_SELECTION,
		);
	}

	onFocus() {
		this.analytics.logEvent(
			AnalyticsEvent.DASHBOARD_HEADER_VIEW_AS_DROPDOWN_OPENED,
		);
	}

	private addressToItemRow(
		{ officeName, id }: BusinessAddress,
		groupType: DefaultDashboardSelectionType,
	): ItemRow {
		return {
			id,
			groupType,
			name: officeName,
		};
	}

	private personToItemRow(
		{ firstName, lastName, id }: BusinessUser,
		groupType: DefaultDashboardSelectionType,
	): ItemRow {
		return {
			id,
			groupType,
			name: getFullName([firstName, lastName]),
		};
	}
}
